import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import AnalyticsDashboard from '../components/AnalyticsDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [trials, setTrials] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create User Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '', plan_type: 'free' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Check if admin is already logged in
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsLoggedIn(true);
      fetchStats();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        setIsLoggedIn(true);
        fetchStats();
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setStats(null);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');
    setCreateLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/users/create`, newUser);
      if (response.data.success) {
        setCreateSuccess(`User ${newUser.email} created with ${newUser.plan_type} plan!`);
        setNewUser({ email: '', password: '', full_name: '', plan_type: 'free' });
        // Refresh data
        fetchStats();
        fetchUsers();
        if (newUser.plan_type !== 'free') {
          fetchSubscriptions();
        }
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowCreateModal(false);
          setCreateSuccess('');
        }, 2000);
      }
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userEmail}`);
      fetchStats();
      fetchUsers();
      fetchSubscriptions();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUpdateSubscription = async (userEmail, planType) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userEmail}/subscription?plan_type=${planType}`);
      fetchSubscriptions();
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/trials`);
      setTrials(response.data.trials);
    } catch (error) {
      console.error('Error fetching trials:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/subscriptions`);
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/groups`);
      setGroups(response.data.groups);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'users') fetchUsers();
    else if (tab === 'trials') fetchTrials();
    else if (tab === 'subscriptions') fetchSubscriptions();
    else if (tab === 'groups') fetchGroups();
  };

  const extendTrial = async (email) => {
    try {
      await axios.post(`${API_URL}/api/admin/trials/${encodeURIComponent(email)}/extend?days=3`);
      fetchTrials();
      alert('Trial extended by 3 days');
    } catch (error) {
      alert('Failed to extend trial');
    }
  };

  const deleteTrial = async (email) => {
    if (window.confirm(`Delete trial for ${email}?`)) {
      try {
        await axios.delete(`${API_URL}/api/admin/trials/${encodeURIComponent(email)}`);
        fetchTrials();
      } catch (error) {
        alert('Failed to delete trial');
      }
    }
  };

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-100 to-purple-100'} flex items-center justify-center p-4`}>
        <div className={`max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8`}>
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔐</div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Admin Dashboard</h1>
            <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Study Quiz Generator
            </p>
          </div>

          {loginError && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-400 rounded-lg text-sm">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Admin Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="admin@studyquiz.com"
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50"
            >
              {loginLoading ? 'Logging in...' : 'Login to Admin'}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className={`w-full mt-4 py-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🔐</span>
              <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-gray-600'}`}
              >
                {isDarkMode ? '☀️' : '🌙'}
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-xl shadow-sm`}>
              <div className="text-3xl mb-2">👥</div>
              <div className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.users.total}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Users</div>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-xl shadow-sm`}>
              <div className="text-3xl mb-2">🎁</div>
              <div className={`text-3xl font-bold text-green-500`}>{stats.trials.active}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active Trials</div>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-xl shadow-sm`}>
              <div className="text-3xl mb-2">💳</div>
              <div className={`text-3xl font-bold text-indigo-500`}>{stats.subscriptions.active}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Subscriptions</div>
            </div>
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-xl shadow-sm`}>
              <div className="text-3xl mb-2">📝</div>
              <div className={`text-3xl font-bold text-purple-500`}>{stats.quizzes.today}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quizzes Today</div>
            </div>
          </div>
        )}

        {/* Tabs and Create User Button */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-2 overflow-x-auto">
            {['overview', 'analytics', 'users', 'trials', 'subscriptions', 'groups'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                  activeTab === tab
                    ? 'bg-indigo-600 text-white'
                    : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center"
          >
            <span className="mr-2">+</span> Create User
          </button>
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl p-6`}>
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Create New User</h2>
                <button onClick={() => { setShowCreateModal(false); setCreateError(''); setCreateSuccess(''); }} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
              </div>

              {createError && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-400 rounded-lg text-sm">
                  {createError}
                </div>
              )}

              {createSuccess && (
                <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-400 text-green-700 dark:text-green-400 rounded-lg text-sm">
                  {createSuccess}
                </div>
              )}

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Full Name</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Password</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Plan Type</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: 'free', name: 'Free', icon: '✨', color: 'gray' },
                      { id: 'student', name: 'Student', icon: '🎓', color: 'indigo' },
                      { id: 'teacher', name: 'Teacher', icon: '👩‍🏫', color: 'emerald' },
                      { id: 'group', name: 'Group', icon: '🏫', color: 'purple' }
                    ].map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setNewUser({...newUser, plan_type: plan.id})}
                        className={`p-2 border-2 rounded-lg text-center transition-all ${
                          newUser.plan_type === plan.id
                            ? plan.id === 'free' ? 'border-gray-500 bg-gray-100 dark:bg-gray-700'
                              : plan.id === 'student' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                              : plan.id === 'teacher' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30'
                              : 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <div className="text-xl">{plan.icon}</div>
                        <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{plan.name}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); setCreateError(''); setCreateSuccess(''); }}
                    className={`flex-1 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    {createLoading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && stats && (
            <div className="space-y-6">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Overview</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Trials</h3>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Active: {stats.trials.active} | Expired: {stats.trials.expired} | Total: {stats.trials.total}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Subscriptions</h3>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Active: {stats.subscriptions.active} | Total: {stats.subscriptions.total}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Quizzes</h3>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Today: {stats.quizzes.today} | Total: {stats.quizzes.total}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Groups</h3>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Total: {stats.groups.total}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <AnalyticsDashboard isDarkMode={isDarkMode} />
          )}

          {/* Users Tab */}
          {activeTab === 'users' && !loading && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-left text-sm`}>
                    <th className="pb-3">Email</th>
                    <th className="pb-3">Name</th>
                    <th className="pb-3">Role</th>
                    <th className="pb-3">Created</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {users.map((user, i) => (
                    <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className="py-3">{user.email}</td>
                      <td className="py-3">{user.name || user.full_name || '-'}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          user.role === 'instructor' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3 text-sm">{user.created_at?.split('T')[0] || '-'}</td>
                      <td className="py-3">
                        <button
                          onClick={() => handleDeleteUser(user.email)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No users found</p>}
            </div>
          )}

          {/* Trials Tab */}
          {activeTab === 'trials' && !loading && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-left text-sm`}>
                    <th className="pb-3">Email</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Days Left</th>
                    <th className="pb-3">Quizzes</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {trials.map((trial, i) => (
                    <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className="py-3">{trial.email}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          trial.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                        }`}>
                          {trial.is_active ? 'Active' : 'Expired'}
                        </span>
                      </td>
                      <td className="py-3">{trial.days_remaining}</td>
                      <td className="py-3">{trial.quizzes_count}</td>
                      <td className="py-3 space-x-2">
                        <button
                          onClick={() => extendTrial(trial.email)}
                          className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          +3 Days
                        </button>
                        <button
                          onClick={() => deleteTrial(trial.email)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {trials.length === 0 && <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No trials found</p>}
            </div>
          )}

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && !loading && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-left text-sm`}>
                    <th className="pb-3">Email</th>
                    <th className="pb-3">Plan</th>
                    <th className="pb-3">Gateway</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Updated</th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {subscriptions.map((sub, i) => (
                    <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className="py-3">{sub.email}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          (sub.plan_type || sub.plan) === 'group' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' : 
                          (sub.plan_type || sub.plan) === 'teacher' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 
                          'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {sub.plan_type || sub.plan || 'student'}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          sub.gateway === 'pesapal' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 
                          sub.gateway === 'paypal' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                          'bg-gray-100 text-gray-700 dark:bg-gray-900/50 dark:text-gray-300'
                        }`}>
                          {sub.gateway || 'unknown'}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          sub.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                        }`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="py-3 text-sm">{sub.updated_at?.split('T')[0] || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscriptions.length === 0 && <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No subscriptions found</p>}
            </div>
          )}

          {/* Groups Tab */}
          {activeTab === 'groups' && !loading && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-left text-sm`}>
                    <th className="pb-3">Name</th>
                    <th className="pb-3">Admin</th>
                    <th className="pb-3">Seats</th>
                    <th className="pb-3">Invite Code</th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {groups.map((group, i) => (
                    <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className="py-3">{group.name}</td>
                      <td className="py-3">{group.admin_email}</td>
                      <td className="py-3">{group.used_seats}/{group.max_seats}</td>
                      <td className="py-3 font-mono text-sm">{group.invite_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {groups.length === 0 && <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No groups found</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
